'use client';

import { useEffect, useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_KEY = 'agencyhub_tour_completed';

/**
 * Inline SVG icon helper — renders a colored badge with icon for driver.js HTML titles
 */
function ic(svg: string, color: string): string {
  return `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;background:${color}18;margin-right:10px;vertical-align:middle;flex-shrink:0"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svg}</svg></span>`;
}

const I = {
  dash: ic('<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>', '#6366f1'),
  scraper: ic('<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>', '#3b82f6'),
  leads: ic('<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>', '#f97316'),
  assets: ic('<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>', '#f59e0b'),
  wa: ic('<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>', '#22c55e'),
  chat: ic('<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>', '#0d9488'),
  proj: ic('<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>', '#8b5cf6'),
  task: ic('<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>', '#f43f5e'),
  inv: ic('<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>', '#10b981'),
};

/**
 * Spotlight tour steps — mengikuti workflow AgencyHUB
 */
const TOUR_STEPS = [
  {
    element: '#nav-dashboard',
    popover: {
      title: `${I.dash}Dashboard`,
      description: 'Pusat kendali Anda. Monitor seluruh funnel — dari jumlah lead, kampanye aktif, hingga revenue dari proyek.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-scraper',
    popover: {
      title: `${I.scraper}Step 1: Scraper Google Maps`,
      description: 'Mulai di sini! Cari ribuan bisnis lokal berdasarkan keyword & lokasi. Filter data, lalu kirim ke Global Data untuk diseleksi dan diimport menjadi Lead.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-leads',
    popover: {
      title: `${I.leads}Step 2: Lead Intelligence`,
      description: 'Lead yang sudah diimport akan di-enrich otomatis: validasi WhatsApp, download foto, discovery IG, dan ekstraksi brand colors. Data ini menjadi bahan baku pembuatan website.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-assets',
    popover: {
      title: `${I.assets}Step 3: Digital Assets`,
      description: 'Build landing page profesional secara otomatis! AI memilih layout, menulis copywriting, dan menyesuaikan warna brand. Hasilnya: website siap pakai untuk calon klien.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-whatsapp',
    popover: {
      title: `${I.wa}Step 4: WA Broadcast`,
      description: 'Buat kampanye WhatsApp untuk menawarkan jasa ke calon klien. LP yang sudah di-build otomatis jadi amunisi pitch. Track status: sent, read, replied.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-livechat',
    popover: {
      title: `${I.chat}Step 5: Live Chat & AI Bot`,
      description: 'Follow-up balasan calon klien. AI Bot menjawab FAQ otomatis, Anda fokus negosiasi serius. Tag lead: Hot, Negosiasi, DEAL — otomatis jadi Project.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-projects',
    popover: {
      title: `${I.proj}Step 6: Project Management`,
      description: 'Kelola proyek klien yang sudah DEAL. Breakdown jadi task, track progress per deliverable, dan buat invoice saat selesai.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-tasks',
    popover: {
      title: `${I.task}To-Do List`,
      description: 'Lihat dan kelola semua tugas dari proyek aktif. Prioritaskan pekerjaan harian Anda di sini.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '#nav-invoices',
    popover: {
      title: `${I.inv}Invoice`,
      description: 'Buat dan kelola tagihan untuk klien. Track status pembayaran dari setiap proyek yang sudah selesai.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
];

/**
 * Custom CSS override for driver.js to match AgencyHUB dark theme
 */
const DARK_THEME_CSS = `
  .driver-popover {
    background: #1e1e1e !important;
    border: none !important;
    border-radius: 8px !important;
    box-shadow: 0 25px 60px rgba(0,0,0,0.5) !important;
    color: #e4e4e7 !important;
    max-width: 420px !important;
    padding: 24px !important;
  }
  .driver-popover * {
    font-family: inherit !important;
  }
  .driver-popover-title {
    font-size: 16px !important;
    font-weight: 700 !important;
    color: #fff !important;
    line-height: 1.4 !important;
    margin-bottom: 8px !important;
    display: flex !important;
    align-items: center !important;
  }
  .driver-popover-description {
    font-size: 13.5px !important;
    line-height: 1.7 !important;
    color: #a1a1aa !important;
  }
  .driver-popover-progress-text {
    font-size: 11px !important;
    color: #71717a !important;
  }
  .driver-popover-footer {
    gap: 10px !important;
    margin-top: 16px !important;
  }
  .driver-popover-footer button {
    font-size: 13px !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    padding: 8px 20px !important;
    text-shadow: none !important;
    transition: all 0.15s ease !important;
  }
  .driver-popover-prev-btn {
    background: transparent !important;
    border: 1px solid rgba(255,255,255,0.1) !important;
    color: #a1a1aa !important;
  }
  .driver-popover-prev-btn:hover {
    background: rgba(255,255,255,0.05) !important;
    color: #fff !important;
  }
  .driver-popover-next-btn,
  .driver-popover-close-btn {
    background: #6366f1 !important;
    border: none !important;
    color: #fff !important;
  }
  .driver-popover-next-btn:hover,
  .driver-popover-close-btn:hover {
    background: #4f46e5 !important;
  }
  .driver-popover-close-btn {
    font-size: 0 !important;
    width: 28px !important;
    height: 28px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    color: #71717a !important;
    border-radius: 50% !important;
  }
  .driver-popover-close-btn::after {
    content: '✕';
    font-size: 14px;
  }
  .driver-popover-close-btn:hover {
    background: rgba(255,255,255,0.08) !important;
    color: #fff !important;
  }
  .driver-popover-arrow-side-left .driver-popover-arrow {
    border-left-color: #1e1e1e !important;
  }
  .driver-popover-arrow-side-right .driver-popover-arrow {
    border-right-color: #1e1e1e !important;
  }
  .driver-popover-arrow-side-top .driver-popover-arrow {
    border-top-color: #1e1e1e !important;
  }
  .driver-popover-arrow-side-bottom .driver-popover-arrow {
    border-bottom-color: #1e1e1e !important;
  }
  .driver-overlay {
    background: transparent !important;
  }
`;

interface UseTourOptions {
  autoStart?: boolean;
  userTourCompleted?: boolean;
}

/** Persist tour dismissal to DB */
async function saveTourToDB() {
  try {
    await fetch('/api/user/tour', { method: 'POST' });
  } catch {
    // Fallback: at least localStorage is set
  }
}

export function useTour({ autoStart = true, userTourCompleted }: UseTourOptions = {}) {
  // Inject dark theme styles once
  useEffect(() => {
    const styleId = 'driver-dark-theme';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = DARK_THEME_CSS;
    document.head.appendChild(style);

    // Inject confirmation popup styles
    const confirmStyleId = 'tour-confirm-styles';
    if (!document.getElementById(confirmStyleId)) {
      const cs = document.createElement('style');
      cs.id = confirmStyleId;
      cs.textContent = `
        .tour-confirm-overlay {
          position: fixed; inset: 0; z-index: 99999;
          background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          animation: tcFadeIn 0.2s ease;
        }
        @keyframes tcFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .tour-confirm-box {
          background: #1e1e1e; border-radius: 8px; padding: 24px;
          max-width: 360px; width: 90%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.6);
        }
        .tour-confirm-box h4 {
          color: #fff; font-size: 15px; font-weight: 700; margin: 0 0 8px;
        }
        .tour-confirm-box p {
          color: #a1a1aa; font-size: 13px; line-height: 1.6; margin: 0 0 20px;
        }
        .tour-confirm-btns {
          display: flex; gap: 10px; justify-content: flex-end;
        }
        .tour-confirm-btns button {
          font-size: 13px; font-weight: 600; border-radius: 6px;
          padding: 8px 18px; cursor: pointer; border: none; transition: all 0.15s;
        }
        .tc-btn-later {
          background: transparent; color: #a1a1aa;
          border: 1px solid rgba(255,255,255,0.1) !important;
        }
        .tc-btn-later:hover { background: rgba(255,255,255,0.05); color: #fff; }
        .tc-btn-dismiss {
          background: #6366f1; color: #fff;
        }
        .tc-btn-dismiss:hover { background: #4f46e5; }
      `;
      document.head.appendChild(cs);
    }
  }, []);

  const showConfirmPopup = useCallback((): Promise<'dismiss' | 'later'> => {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'tour-confirm-overlay';
      overlay.innerHTML = `
        <div class="tour-confirm-box">
          <h4>Tutup Tour Panduan?</h4>
          <p>Apakah Anda ingin menonaktifkan tour ini agar tidak muncul lagi saat login?</p>
          <div class="tour-confirm-btns">
            <button class="tc-btn-later">Nanti Saja</button>
            <button class="tc-btn-dismiss">Jangan Munculkan Lagi</button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);

      overlay.querySelector('.tc-btn-later')!.addEventListener('click', () => {
        overlay.remove();
        resolve('later');
      });
      overlay.querySelector('.tc-btn-dismiss')!.addEventListener('click', () => {
        overlay.remove();
        resolve('dismiss');
      });
    });
  }, []);

  const startTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: 'Lanjut →',
      prevBtnText: '← Kembali',
      doneBtnText: 'Selesai ✨',
      progressText: '{{current}} dari {{total}}',
      animate: true,
      smoothScroll: true,
      overlayOpacity: 0,
      stagePadding: 10,
      stageRadius: 10,
      popoverOffset: 20,
      steps: TOUR_STEPS,
      onDestroyStarted: async () => {
        const isLastStep = driverObj.isLastStep();
        driverObj.destroy();

        if (isLastStep) {
          // Selesai semua step → simpan permanen
          localStorage.setItem(TOUR_KEY, 'true');
          saveTourToDB();
        } else {
          // Klik close di tengah jalan → tanya konfirmasi
          const result = await showConfirmPopup();
          if (result === 'dismiss') {
            localStorage.setItem(TOUR_KEY, 'true');
            saveTourToDB();
          }
          // 'later' → tidak simpan, akan muncul lagi next visit
        }
      },
    });

    driverObj.drive();
  }, [showConfirmPopup]);

  // Auto-start on first visit (check localStorage + DB flag)
  useEffect(() => {
    if (!autoStart) return;
    if (userTourCompleted) return; // DB says completed
    const localCompleted = localStorage.getItem(TOUR_KEY);
    if (localCompleted) return;

    const timer = setTimeout(() => {
      startTour();
    }, 800);

    return () => clearTimeout(timer);
  }, [autoStart, startTour, userTourCompleted]);

  return { startTour };
}

